import useOfferList from 'hooks/Offers/useOfferList'
import type { ComponentProps, PropsWithChildren } from 'react'
import React, { useCallback, useEffect, useRef } from 'react'
import { useIntl } from 'react-intl'
import { useInView } from 'react-intersection-observer'
import OfferListCarouselTrack from 'components/OfferList/OfferListCarouselTrack'
import styled from 'styled-components'
import LayoutContainer from 'components/Common/LayoutContainer'
import { rem } from 'polished'
import Group from 'components/utils/Group'
import BodyText from 'LuxKit/Typography/BodyText'
import OfferSortControl from 'components/Common/OfferSortControl'
import useOfferListSortOptions from 'hooks/useOfferListSortOptions'
import type { SortControlChangeHandler } from 'components/Common/OfferSortControl/OfferSortControl'
import { useAppDispatch } from 'hooks/reduxHooks'
import { replace } from 'connected-react-router'
import useQueryParams from 'hooks/useQueryParams'
import { mediaQueryUp } from 'components/utils/breakpoint'

const Container = styled(LayoutContainer)`
  padding-top: ${rem(40)};
  padding-bottom: ${rem(20)};
`

const GridWrapper = styled.div`
  row-gap: ${rem(40)};
  column-gap: ${rem(24)};

  ${mediaQueryUp.tablet} {
    display: grid;
    grid-template-columns: 1fr 1fr;
  }

  ${mediaQueryUp.desktop} {
    grid-template-columns: 1fr 1fr 1fr;
  }

  > * {
    width: 100%;
  }
`

type InheritedOfferListCarouselTrackProps = Pick<ComponentProps<typeof OfferListCarouselTrack>, 'additionalOfferUrlQuery' | 'tabletLimit' | 'limit' | 'tileStyle'>

interface Props extends PropsWithChildren, InheritedOfferListCarouselTrackProps {
  /**
   * The set of filters to fetch the offer list with
   */
  filters?: App.OfferListFilters
  /**
   * Use this to provide your own full offer list for the carousel to display
   */
  overrideOfferList?: App.OfferList
  /**
   * Exclude the following offer ids from the results
   */
  excludedIds?: Array<string>
  /**
   * Override the duration and the price with the checkIn/checkOut/Occupancy
   */
  selectedDatesAndRoomsFilter?: App.OfferListFilters
}

function ContentfulOfferGrid(props: Props) {
  const intl = useIntl()
  const { searchSortByTypes } = useOfferListSortOptions()
  const {
    filters,
    overrideOfferList,
    limit,
    tabletLimit,
    children,
    tileStyle,
    additionalOfferUrlQuery,
    excludedIds,
    selectedDatesAndRoomsFilter,
  } = props
  const dispatch = useAppDispatch()
  const handleSortChange = useCallback<SortControlChangeHandler>((params) => {
    dispatch(replace({
      search: params.search,
    }))
  }, [dispatch])
  const query = useQueryParams()
  const sortBy = (query.get('sortBy') ?? 'recommended') as App.OfferListSortByOptions

  const filterOfferList = useOfferList(
    { ...filters, sortBy },
    {
      disabled: !!overrideOfferList || !filters,
      excludedIds,
      minRequiredOffers: filters?.limit,
    },
  )
  const offerList = overrideOfferList ?? filterOfferList
  const carouselRef = useRef<HTMLDivElement | null>(null)

  const [inViewRef, carouselInView] = useInView({
    triggerOnce: true,
    rootMargin: '500px',
  })
  useEffect(() => {
    // We've been given a new offer list, send the carousel back to the start
    if (carouselRef.current) {
      carouselRef.current.scrollTo({ left: 0 })
    }
  }, [offerList])

  useEffect(() => {
    inViewRef(carouselRef.current)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [carouselRef.current])

  const track = <OfferListCarouselTrack
    inView={carouselInView}
    offerList={offerList}
    tileStyle={tileStyle}
    additionalOfferUrlQuery={additionalOfferUrlQuery}
    limit={limit}
    tabletLimit={tabletLimit}
    selectedDatesAndRoomsFilter={selectedDatesAndRoomsFilter}
  >
    {children}
  </OfferListCarouselTrack>

  const countText = intl.formatMessage(
    {
      id: 'rGApPzAcMx',
      defaultMessage: 'Showing {count, plural, one {# offer} other {# offers}}',
      description: 'Count of offers displayed in contentful offer grid view',
    },
    { count: offerList.offerIds?.length ?? 0 },
  )

  return <Container>
    <Group direction="vertical" gap={32}>
      <Group direction="horizontal" horizontalAlign="space-between" verticalAlign="center">
        <BodyText variant="medium">{countText}</BodyText>
        <OfferSortControl sortOptions={searchSortByTypes} onSortChange={handleSortChange} />
      </Group>
      <GridWrapper>
        {track}
      </GridWrapper>
    </Group>
  </Container>
}

export default React.memo(ContentfulOfferGrid)
